/**
 * generateReport.ts
 *
 * Generates a branded TRACE analysis-report PDF entirely in the browser
 * using jsPDF.  The PDF includes:
 *   – Header with logo, document title and metadata
 *   – Analysis overview
 *   – Category breakdown
 *   – Full findings list with severity, AI comments & recommendations
 *   – Branded footer on every page
 */
import { jsPDF } from 'jspdf';

/* ── Brand palette ──────────────────────────────────────────────── */
const BRAND = {
    teal: [52, 190, 189] as [number, number, number],
    dark: [34, 34, 34] as [number, number, number],
    text: [51, 65, 85] as [number, number, number],
    muted: [148, 163, 184] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    lightBg: [248, 250, 252] as [number, number, number],
    green: [34, 197, 94] as [number, number, number],
    amber: [245, 158, 11] as [number, number, number],
    red: [239, 68, 68] as [number, number, number],
    blue: [59, 130, 246] as [number, number, number],
    violet: [139, 92, 246] as [number, number, number],
    surface100: [241, 245, 249] as [number, number, number],
};

const SEVERITY_COLORS: Record<string, [number, number, number]> = {
    critical: BRAND.red,
    high: BRAND.amber,
    medium: BRAND.blue,
    low: BRAND.muted,
};

const CATEGORY_LABELS: Record<string, string> = {
    data_mismatch: 'Data Mismatch',
    signature_mismatch: 'Signature Mismatch',
    test_requirement: 'Test Requirement',
    formatting: 'Formatting',
    compliance_gap: 'Compliance Gap',
};

const QUALITY_LABELS: Record<string, string> = {
    satisfactory: 'Satisfactory',
    needs_improvement: 'Needs Improvement',
    unsatisfactory: 'Unsatisfactory',
};

const QUALITY_COLORS: Record<string, [number, number, number]> = {
    satisfactory: BRAND.green,
    needs_improvement: BRAND.amber,
    unsatisfactory: BRAND.red,
};

/* ── Logo to data-URL via off-screen canvas ─────────────────────── */
async function loadLogoDataUrl(): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        // using the SVG that CRA will inline as a URL when imported
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || 643;
            canvas.height = img.naturalHeight || 209;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(null); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        // Use the raw SVG file — CRA serves it from /static/media/...
        // We import it below separately so this file has no React dependency.
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const src = require('../assets/trace-logo.svg');
            // CRA may return { ReactComponent, default: url } or just a string
            img.src = typeof src === 'string' ? src : (src.default || src);
        } catch {
            resolve(null);
        }
    });
}

/* ── Helpers ────────────────────────────────────────────────────── */
const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_H = 14;

type Doc = jsPDF;

function addFooter(pdf: Doc, pageNum: number) {
    const y = PAGE_H - FOOTER_H;
    // thin line
    pdf.setDrawColor(...BRAND.surface100);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
    // left text
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...BRAND.muted);
    pdf.text('Generated by TRACE — Document Analysis Platform', MARGIN, y + 5);
    // right text
    const pageText = `Page ${pageNum}`;
    pdf.text(pageText, PAGE_W - MARGIN - pdf.getTextWidth(pageText), y + 5);
}

/** Ensure enough vertical space; add page if not. Returns the current Y. */
function ensureSpace(pdf: Doc, y: number, needed: number, pageNum: { n: number }): number {
    if (y + needed > PAGE_H - FOOTER_H - 4) {
        addFooter(pdf, pageNum.n);
        pageNum.n++;
        pdf.addPage();
        return MARGIN + 4;
    }
    return y;
}

/** Wrap text and print, returning new Y. */
function writeWrapped(
    pdf: Doc, text: string, x: number, y: number, maxW: number,
    fontSize: number, color: [number, number, number], style: string,
    pageCounter: { n: number },
    lineHeight = 1.4,
): number {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...color);
    const lines = pdf.splitTextToSize(text, maxW) as string[];
    const lh = fontSize * 0.3528 * lineHeight; // pt→mm × line-height
    for (const line of lines) {
        y = ensureSpace(pdf, y, lh + 1, pageCounter);
        pdf.text(line, x, y);
        y += lh;
    }
    return y;
}

/* ── Main export ────────────────────────────────────────────────── */
export interface ReportDocument {
    fileName: string;
    docName: string;
    templateVersion?: string;
    templateFileName?: string;
    uploadDate: string;
    fileSize?: string;
    qualityRating?: string;
    issuesDetected?: number;
    analysisOverview?: string;
    findings?: {
        id: string;
        category: string;
        severity: string;
        section: string;
        title: string;
        description: string;
        aiComment?: string;
        recommendation?: string;
    }[];
}

export async function generateReport(doc: ReportDocument): Promise<void> {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const page = { n: 1 };

    /* ── 1. Header band ─────────────────────────────────────────── */
    pdf.setFillColor(...BRAND.white);
    pdf.rect(0, 0, PAGE_W, 42, 'F');

    // Logo
    const logoUrl = await loadLogoDataUrl();
    if (logoUrl) {
        // original aspect ratio ≈ 643:209 ≈ 3.08 : 1
        const logoH = 12;
        const logoW = logoH * 3.08;
        pdf.addImage(logoUrl, 'PNG', MARGIN, 8, logoW, logoH);
    } else {
        // Fallback text logo
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.setTextColor(...BRAND.teal);
        pdf.text('TRACE', MARGIN, 17);
    }

    // Subtitle under logo
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...BRAND.text);
    pdf.text('Document Analysis Report', MARGIN, 25);

    // Date on right
    const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
    pdf.setFontSize(8);
    pdf.setTextColor(...BRAND.muted);
    pdf.text(dateStr, PAGE_W - MARGIN - pdf.getTextWidth(dateStr), 17);

    // Teal accent bar
    pdf.setFillColor(...BRAND.teal);
    pdf.rect(0, 42, PAGE_W, 1.5, 'F');

    /* ── 2. Document info section ───────────────────────────────── */
    let y = 52;

    // Document title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(...BRAND.dark);
    pdf.text(doc.fileName, MARGIN, y);
    y += 7;

    // Metadata row
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...BRAND.muted);
    const metaParts = [
        doc.docName,
        new Date(doc.uploadDate).toLocaleDateString(),
        doc.fileSize || '',
    ].filter(Boolean).join('   |   ');
    pdf.text(metaParts, MARGIN, y);
    y += 8;

    // ── Document Source Labels ──────────────────────────────────
    // Analysed Document badge
    const analysedLabel = 'ANALYSED DOCUMENT';
    pdf.setFillColor(239, 246, 255); // light blue bg
    const adBoxW = CONTENT_W / 2 - 2;
    pdf.roundedRect(MARGIN, y - 3, adBoxW, 18, 2, 2, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.setTextColor(59, 130, 246); // blue text
    pdf.text(analysedLabel, MARGIN + 4, y + 1);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...BRAND.dark);
    const adFileName = doc.fileName.length > 40 ? doc.fileName.substring(0, 37) + '...' : doc.fileName;
    pdf.text(adFileName, MARGIN + 4, y + 7);
    pdf.setFontSize(6.5);
    pdf.setTextColor(...BRAND.muted);
    pdf.text(`${doc.fileSize || ''} · ${new Date(doc.uploadDate).toLocaleDateString()}`, MARGIN + 4, y + 12);

    // KB Template badge
    const kbLabel = 'KNOWLEDGE BASE TEMPLATE';
    const kbX = MARGIN + adBoxW + 4;
    pdf.setFillColor(245, 243, 255); // light violet bg
    pdf.roundedRect(kbX, y - 3, adBoxW, 18, 2, 2, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.setTextColor(139, 92, 246); // violet text
    pdf.text(kbLabel, kbX + 4, y + 1);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...BRAND.dark);
    if (doc.templateFileName) {
        const tplName = doc.templateFileName.length > 40 ? doc.templateFileName.substring(0, 37) + '...' : doc.templateFileName;
        pdf.text(tplName, kbX + 4, y + 7);
        pdf.setFontSize(6.5);
        pdf.setTextColor(...BRAND.muted);
        pdf.text(doc.templateVersion || '', kbX + 4, y + 12);
    } else {
        const noTpl = (doc.templateVersion === 'Auto-detect' || !doc.templateVersion)
            ? 'None — AI General Knowledge'
            : doc.templateVersion;
        pdf.text(noTpl, kbX + 4, y + 7);
        pdf.setFontSize(6.5);
        pdf.setTextColor(...BRAND.muted);
        pdf.text('No template file uploaded', kbX + 4, y + 12);
    }
    y += 22;

    // Quality badge
    if (doc.qualityRating) {
        const qColor = QUALITY_COLORS[doc.qualityRating] || BRAND.muted;
        const qLabel = QUALITY_LABELS[doc.qualityRating] || doc.qualityRating;
        y += 3;
        pdf.setFillColor(...qColor);
        const badgeW = pdf.getTextWidth(qLabel) * 1.6 + 8;
        pdf.roundedRect(MARGIN, y - 4, badgeW, 7, 1.5, 1.5, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(...BRAND.white);
        pdf.text(qLabel.toUpperCase(), MARGIN + 4, y);
        y += 6;
    }

    // Thin divider
    y += 3;
    pdf.setDrawColor(...BRAND.surface100);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 6;

    /* ── 3. Analysis overview ───────────────────────────────────── */
    if (doc.analysisOverview) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(...BRAND.teal);
        pdf.text('Analysis Overview', MARGIN, y);
        y += 6;

        // Light box
        const overviewLines = pdf.splitTextToSize(doc.analysisOverview, CONTENT_W - 10) as string[];
        const boxH = overviewLines.length * 4.5 + 8;
        pdf.setFillColor(...BRAND.lightBg);
        pdf.roundedRect(MARGIN, y - 3, CONTENT_W, boxH, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(...BRAND.text);
        overviewLines.forEach((line: string) => {
            pdf.text(line, MARGIN + 5, y + 2);
            y += 4.5;
        });
        y += 8;
    }

    /* ── 4. Category breakdown ──────────────────────────────────── */
    const findings = doc.findings || [];
    const cats: Record<string, number> = {};
    findings.forEach(f => { cats[f.category] = (cats[f.category] || 0) + 1; });
    const catEntries = Object.entries(cats).sort((a, b) => b[1] - a[1]);

    if (catEntries.length > 0) {
        y = ensureSpace(pdf, y, 28, page);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(...BRAND.teal);
        pdf.text(`Issues Detected: ${doc.issuesDetected ?? findings.length}`, MARGIN, y);
        y += 6;

        // Bar chart style
        const barMaxW = CONTENT_W - 50;
        const maxCount = Math.max(...catEntries.map(c => c[1]));
        catEntries.forEach(([cat, count]) => {
            y = ensureSpace(pdf, y, 8, page);
            const label = CATEGORY_LABELS[cat] || cat;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(...BRAND.text);
            pdf.text(label, MARGIN, y);
            // bar
            const barW = Math.max((count / maxCount) * barMaxW, 4);
            pdf.setFillColor(...BRAND.teal);
            pdf.roundedRect(MARGIN + 42, y - 3.2, barW, 4.5, 1, 1, 'F');
            // count text
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...BRAND.dark);
            pdf.text(String(count), MARGIN + 42 + barW + 3, y);
            y += 7;
        });
        y += 4;
    }

    /* ── 5. Detailed findings ───────────────────────────────────── */
    if (findings.length > 0) {
        y = ensureSpace(pdf, y, 14, page);
        pdf.setDrawColor(...BRAND.surface100);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
        y += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(...BRAND.dark);
        pdf.text('Detailed Findings', MARGIN, y);
        y += 8;

        findings.forEach((f, idx) => {
            y = ensureSpace(pdf, y, 30, page);

            // Finding number + severity colour bar
            const sevColor = SEVERITY_COLORS[f.severity] || BRAND.muted;
            pdf.setFillColor(...sevColor);
            pdf.rect(MARGIN, y - 3.5, 2, 16, 'F');

            // Title
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9.5);
            pdf.setTextColor(...BRAND.dark);
            pdf.text(`${idx + 1}. ${f.title}`, MARGIN + 5, y);
            y += 4.5;

            // Severity + category + section
            const sevLabel = f.severity.charAt(0).toUpperCase() + f.severity.slice(1);
            const catLabel = CATEGORY_LABELS[f.category] || f.category;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7.5);
            pdf.setTextColor(...sevColor);
            pdf.text(sevLabel.toUpperCase(), MARGIN + 5, y);
            pdf.setTextColor(...BRAND.muted);
            pdf.text(`   •   ${catLabel}   •   Section ${f.section}`, MARGIN + 5 + pdf.getTextWidth(sevLabel.toUpperCase()), y);
            y += 5;

            // Description
            y = writeWrapped(pdf, f.description, MARGIN + 5, y, CONTENT_W - 10, 8.5, BRAND.text, 'normal', page);
            y += 2;

            // AI Comment
            if (f.aiComment) {
                y = ensureSpace(pdf, y, 10, page);
                pdf.setFont('helvetica', 'bolditalic');
                pdf.setFontSize(7.5);
                pdf.setTextColor(...BRAND.teal);
                pdf.text('TRACE AI Comment:', MARGIN + 5, y);
                y += 3.5;
                y = writeWrapped(pdf, f.aiComment, MARGIN + 5, y, CONTENT_W - 10, 8, BRAND.text, 'italic', page);
                y += 2;
            }

            // Recommendation
            if (f.recommendation) {
                y = ensureSpace(pdf, y, 10, page);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(7.5);
                pdf.setTextColor(...BRAND.green);
                pdf.text('Recommendation:', MARGIN + 5, y);
                y += 3.5;
                y = writeWrapped(pdf, f.recommendation, MARGIN + 5, y, CONTENT_W - 10, 8, BRAND.text, 'normal', page);
            }

            y += 6;
        });
    }

    /* ── Footer on last page ────────────────────────────────────── */
    addFooter(pdf, page.n);

    /* ── Save ────────────────────────────────────────────────────── */
    const safeName = doc.fileName.replace(/[^a-zA-Z0-9_.-]/g, '_');
    pdf.save(`TRACE_Report_${safeName}.pdf`);
}
